import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { logo } from '../assets/media';
import '../ReceiptPortal.css';

const API = '/api';

export default function ReceiptPortal() {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError]     = useState('');
  const [file, setFile]       = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone]       = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    fetch(`${API}/receipt/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setReceipt(data);
      })
      .catch(() => setError('Unable to load receipt. Please try again.'));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openReceipt = async () => {
    try {
      const res = await fetch(`${API}/receipt/${token}/html`);
      if (!res.ok) { alert('Receipt not available.'); return; }
      const html = await res.text();
      const w = window.open('', '_blank');
      if (!w) { alert('Popup blocked — please allow popups and try again.'); return; }
      w.document.write(html);
      w.document.close();
    } catch { alert('Failed to load receipt. Please try again.'); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/receipt/${token}/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const sym = (cur) => cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur === 'GBP' ? '£' : (cur || '€');

  if (error) return (
    <div className="rp-root">
      <div className="rp-bg" />
      <div className="rp-wrap">
        <div className="rp-header">
          <img src={logo} alt="7 Elemente" className="rp-logo" />
        </div>
        <div className="rp-card">
          <div className="rp-result">
            <div className="rp-result-icon rp-result-icon--error">!</div>
            <h2 className="rp-result-title">Link Invalid</h2>
            <p className="rp-result-body">{error}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!receipt) return (
    <div className="rp-root">
      <div className="rp-bg" />
      <div className="rp-wrap">
        <div className="rp-header">
          <img src={logo} alt="7 Elemente" className="rp-logo" />
        </div>
        <div className="rp-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <span className="rp-spinner rp-spinner-lg" />
        </div>
      </div>
    </div>
  );

  if (done || receipt.status === 'signed' || receipt.status === 'documented') return (
    <div className="rp-root">
      <div className="rp-bg" />
      <div className="rp-wrap">
        <div className="rp-header">
          <img src={logo} alt="7 Elemente" className="rp-logo" />
          <span className="rp-header-tag">Safe-Pay Portal</span>
        </div>
        <div className="rp-card">
          <div className="rp-result">
            <div className="rp-result-icon rp-result-icon--success">✓</div>
            <h2 className="rp-result-title">Receipt Signed & Returned</h2>
            <p className="rp-result-body">
              Your signed receipt for <strong>{receipt.receipt_number}</strong> has been received.<br/>
              {receipt.status === 'documented'
                ? 'This payment is now fully documented.'
                : '7 Elemente will review and mark it as documented shortly.'}
            </p>
            <p className="rp-result-hint">
              Receipt: <strong>{receipt.receipt_number}</strong><br/>
              Amount: <strong>{sym(receipt.currency)}{Number(receipt.amount).toFixed(2)} {receipt.currency}</strong><br/>
              Project: <strong>{receipt.project_name}</strong>
            </p>
          </div>
        </div>
        <p className="rp-footer">7ELEMENTE IT SOLUTIONS · info@7elemente.com</p>
      </div>
    </div>
  );

  return (
    <div className="rp-root">
      <div className="rp-bg" />
      <div className="rp-wrap">
        <div className="rp-header">
          <img src={logo} alt="7 Elemente" className="rp-logo" />
          <span className="rp-header-tag">Safe-Pay Portal</span>
        </div>

        <div className="rp-card">
          {/* Receipt summary */}
          <div className="rp-section">
            <h2 className="rp-title">Payment Receipt</h2>
            <p className="rp-subtitle">Review your payment details, print & sign the receipt, then upload the signed copy below.</p>

            <div className="rp-summary">
              <div className="rp-summary-row">
                <span>Receipt No.</span>
                <strong className="rp-mono">{receipt.receipt_number}</strong>
              </div>
              <div className="rp-summary-row">
                <span>Related NDA</span>
                <strong className="rp-mono">{receipt.nda_number}</strong>
              </div>
              <div className="rp-summary-row">
                <span>Amount</span>
                <strong className="rp-amount">{sym(receipt.currency)}{Number(receipt.amount).toFixed(2)} {receipt.currency}</strong>
              </div>
              <div className="rp-summary-row">
                <span>Service Period</span>
                <strong>{receipt.service_date}</strong>
              </div>
              <div className="rp-summary-row">
                <span>Project</span>
                <strong>{receipt.project_name}</strong>
              </div>
              {receipt.description && (
                <div className="rp-summary-row">
                  <span>Description</span>
                  <strong>{receipt.description}</strong>
                </div>
              )}
            </div>

            <button className="rp-btn rp-btn-outline" onClick={openReceipt}>
              📄 Open &amp; Print Receipt
            </button>
          </div>

          <div className="rp-divider" />

          {/* Upload section */}
          <div className="rp-section">
            <h3 className="rp-section-title">Upload Signed Copy</h3>
            <p className="rp-section-sub">Print the receipt, sign it, then upload a scan or photo of the signed document.</p>

            <div
              className={`rp-upload-box${file ? ' rp-upload-box--ready' : ''}`}
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <>
                  <span className="rp-upload-icon">📎</span>
                  <span className="rp-upload-name">{file.name}</span>
                  <span className="rp-upload-change">Click to change</span>
                </>
              ) : (
                <>
                  <span className="rp-upload-icon">⬆</span>
                  <span className="rp-upload-label">Click to select signed receipt</span>
                  <span className="rp-upload-hint">PDF, JPG or PNG · max 20 MB</span>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0] || null)}
              />
            </div>

            {error && <div className="rp-error">{error}</div>}

            <div className="rp-actions">
              <button
                className="rp-btn"
                disabled={!file || uploading}
                onClick={handleUpload}
              >
                {uploading ? <span className="rp-spinner" /> : null}
                {uploading ? 'Uploading…' : 'Submit Signed Receipt →'}
              </button>
            </div>

            <p className="rp-disclaimer">
              By uploading, you confirm receipt of the payment and agree to the terms stated in the receipt document.
            </p>
          </div>
        </div>

        <p className="rp-footer">7ELEMENTE IT SOLUTIONS · GOSLAR, GERMANY · info@7elemente.com</p>
      </div>
    </div>
  );
}
